
import { useState, useRef } from 'react';

interface UndoState<T> {
  data: T;
  timestamp: number;
  action: string;
}

export const useUndoHistory = <T>(initialState: T) => {
  const [currentState, setCurrentState] = useState<T>(initialState);
  const [canUndo, setCanUndo] = useState(false);
  const [showUndoButton, setShowUndoButton] = useState(false);
  const lastState = useRef<UndoState<T> | null>(null);
  const undoTimeout = useRef<NodeJS.Timeout | null>(null);

  const saveStateForUndo = (newState: T, action: string) => {
    // Save current state before making changes
    lastState.current = {
      data: currentState,
      timestamp: Date.now(),
      action
    };
    
    setCurrentState(newState);
    setCanUndo(true);
    setShowUndoButton(true);
    
    // Hide undo button after 5 seconds
    if (undoTimeout.current) {
      clearTimeout(undoTimeout.current);
    }
    undoTimeout.current = setTimeout(() => {
      setShowUndoButton(false);
    }, 5000);
  };

  const undo = () => {
    if (lastState.current && canUndo) {
      setCurrentState(lastState.current.data);
      setCanUndo(false);
      setShowUndoButton(false);
      lastState.current = null;
      
      if (undoTimeout.current) {
        clearTimeout(undoTimeout.current);
      }
    }
  };

  const hideUndo = () => {
    setShowUndoButton(false);
    if (undoTimeout.current) {
      clearTimeout(undoTimeout.current);
    }
  };

  return {
    state: currentState,
    setState: setCurrentState,
    saveStateForUndo,
    undo,
    canUndo: canUndo && showUndoButton,
    lastAction: lastState.current?.action || '',
    hideUndo
  };
};
